import React, { useEffect, useState, useRef } from 'react';
import { Input, Box, VStack, HStack, Icon, Text, Spinner, Center, Highlight } from '@chakra-ui/react';
import { LuMapPin } from 'react-icons/lu';
import { useController, Control, FieldValues, Path } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { searchPlacesMapsAutocompleteGetOptions } from '@/client/@tanstack/react-query.gen';
import type { PlacePrediction } from '@/client/types.gen';
import { makeLogger } from '@/utils/logger/logger.ts';

interface AddressAutocompleteProps<TFormValues extends FieldValues> {
	name: Path<TFormValues>;
	control: Control<TFormValues>;
	placeholder?: string;
	country?: string;
	required?: boolean;
	autoFocus?: boolean;
	onAddressSelect?: (prediction: PlacePrediction) => void;
	/** Label element passed from Field component for floating label support */
	labelElement?: React.ReactNode;
	/**
	 * Forwarded className from Field. Field injects `peer` here to enable floating label selectors.
	 */
	className?: string;
	/**
	 * Any additional props passed from Field (e.g., rounded, focusRingWidth) should be forwarded to Input
	 */
	[key: string]: any;
}

const logger = makeLogger('AddressAutocomplete');

/** Shared highlight styling for the matched query within both the main and secondary suggestion text. */
const HIGHLIGHT_STYLES = { bg: 'yellow.100', color: 'brand.fg', fontWeight: 'medium' } as const;

function getHighlightWords(inputValue: string) {
	return inputValue
		.trim() // Remove leading/trailing whitespace
		.split(/\s+/) // Split by one or more whitespace characters
		.filter(Boolean); // Remove empty strings
}

/**
 * Custom address autocomplete component using Google Maps API
 *
 * Provides real-time address suggestions with keyboard navigation support.
 * Integrates seamlessly with React Hook Form and follows Chakra UI v3 patterns.
 */
export const AddressAutocomplete = <TFormValues extends FieldValues>({
	name,
	control,
	placeholder = ' ',
	country = 'nz',
	required = true,
	autoFocus = false,
	onAddressSelect,
	labelElement,
	className,
	...rest
}: AddressAutocompleteProps<TFormValues>) => {
	const { field } = useController({
		name,
		control,
		rules: required ? { required: 'Address is required' } : undefined,
	});

	const [inputValue, setInputValue] = useState(String(field.value || ''));
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const [isUserTyping, setIsUserTyping] = useState(false);

	const inputRef = useRef<HTMLInputElement>(null);

	const highlightWords = getHighlightWords(inputValue);

	useEffect(() => {
		if (!autoFocus) return;

		const frameId = requestAnimationFrame(() => {
			inputRef.current?.focus();
		});

		return () => cancelAnimationFrame(frameId);
	}, [autoFocus]);

	// Use React Query with debouncing - only fetch when input is >= 3 characters
	const { data: autocompleteData, isLoading } = useQuery({
		...searchPlacesMapsAutocompleteGetOptions({
			query: {
				query: inputValue,
				country,
				types: 'address',
			},
		}),
		enabled: inputValue.length >= 3,
		staleTime: 5 * 60 * 1000, // 5 minutes cache
	});

	const predictions: PlacePrediction[] = autocompleteData?.predictions || [];

	const handleInputChange = (value: string) => {
		logger.info(`[handleInputChange] ${value}`);
		setInputValue(value);
		field.onChange(value);
		setShowSuggestions(value.length >= 3);
		setSelectedIndex(-1);
		setIsUserTyping(true);
	};

	const handleSuggestionSelect = (prediction: PlacePrediction) => {
		logger.info(`[handleSuggestionSelect] ${prediction}`);
		const address = prediction.description;
		setInputValue(address);
		field.onChange(address);
		setShowSuggestions(false);
		setSelectedIndex(-1);
		setIsUserTyping(false);
		onAddressSelect?.(prediction);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		logger.info(`[handleKeyDown] ${e.key}`);
		if (!showSuggestions || predictions.length === 0) return;

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setSelectedIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : 0));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setSelectedIndex((prev) => (prev > 0 ? prev - 1 : predictions.length - 1));
				break;
			case 'Enter':
				e.preventDefault();
				if (selectedIndex >= 0) {
					handleSuggestionSelect(predictions[selectedIndex]);
				}
				break;
			case 'Escape':
				setShowSuggestions(false);
				setSelectedIndex(-1);
				break;
		}
	};

	/** Delay hiding suggestions to allow for clicks */
	const handleBlur = () => {
		logger.info(`[handleBlur]`);
		// Reduced timeout and only hide if not clicking on suggestions
		setTimeout(() => {
			setShowSuggestions(false);
			setSelectedIndex(-1);
		}, 100);
	};

	const handleFocus = () => {
		logger.info(`[handleFocus]`);
		if (inputValue.length >= 3 && predictions.length > 0) {
			setShowSuggestions(true);
		}
	};

	return (
		<Box pos="relative" w="full" display="inline-flex" alignItems="center" justifyContent="flex-start">
			<Input
				ref={inputRef}
				value={inputValue}
				onChange={(e) => handleInputChange(e.target.value)}
				onKeyDown={handleKeyDown}
				onBlur={handleBlur}
				onFocus={handleFocus}
				placeholder={placeholder}
				autoFocus={autoFocus}
				autoComplete="off"
				className={className}
				{...rest}
			/>
			{labelElement}

			{(showSuggestions && predictions.length > 0) || (isLoading && isUserTyping && inputValue.length >= 3) ? (
				<Box
					layerStyle="card.raised"
					position="absolute"
					top="100%"
					left={0}
					right={0}
					zIndex={1000}
					maxH="500px"
					overflowY="auto"
					mt={2}
				>
					{isLoading && isUserTyping && inputValue.length >= 3 && predictions.length === 0 && (
						<Center p={3} minH="150px">
							<Spinner size="lg" />
						</Center>
					)}

					{predictions.length > 0 && (
						<VStack gap={0} align="stretch">
							{predictions.map((prediction: PlacePrediction, index: number) => {
								const mainText =
									String(prediction.structured_formatting?.main_text) || prediction.description;
								const secondaryText = prediction.structured_formatting?.secondary_text
									? String(prediction.structured_formatting.secondary_text)
									: null;

								return (
									<Box
										key={prediction.place_id}
										p={3}
										cursor="pointer"
										bg={index === selectedIndex ? 'surface.subtle' : 'transparent'}
										_hover={{ bg: 'surface.subtle' }}
										onMouseDown={(e) => {
											e.preventDefault(); // Prevent blur from firing
											handleSuggestionSelect(prediction);
										}}
										borderBottom={index < predictions.length - 1 ? '1px solid' : 'none'}
										borderColor="border.default"
									>
										<HStack gap={3} align="center">
											<Icon
												as={LuMapPin}
												boxSize={4}
												color="fg.muted"
												opacity={0.5}
												flexShrink={0}
											/>
											<Box>
												<Text textStyle="body.strong">
													<Highlight
														ignoreCase={true}
														query={highlightWords}
														styles={HIGHLIGHT_STYLES}
													>
														{mainText}
													</Highlight>
												</Text>
												{secondaryText && (
													<Text textStyle="body.sm" color="fg.muted">
														<Highlight
															ignoreCase={true}
															query={highlightWords}
															styles={HIGHLIGHT_STYLES}
														>
															{secondaryText}
														</Highlight>
													</Text>
												)}
											</Box>
										</HStack>
									</Box>
								);
							})}
						</VStack>
					)}
				</Box>
			) : null}
		</Box>
	);
};
